import * as React from 'react';
import * as cookie from 'react-cookies';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import {
  getBaseUrl,
  getUrl,
  isCompanyOrChildCompany,
  isSchoolOrTeacher,
} from '../../../../utilities';
import {
  FURNITURE_CATEGORY_GROUP_UNIQID,
  URL_ESHOP_ENTRY,
  URL_FOR_ORGANIZATIONS,
  URL_FOR_SCHOOLS,
  URL_FURNITURE_SCHOOLS,
  URL_SET,
  URL_SETS,
} from '../../../../constants';
import {
  CallIcon,
  MessagesIcon,
  PresentationChartIcon,
  ProfileIcon,
  ShopIcon,
  TeacherIcon,
} from './icons';

export type AudienceKey = 'self' | 'schools' | 'organizations';

export interface MenuNode {
  id?: number;
  name?: string;
  url?: string;
  link?: string;
  uniqid?: string;
  webshow?: number;
  children?: MenuNode[];
}

export interface SubmenuChild {
  key: string;
  label: string;
  to: string;
  uniqid?: string;
}

export interface SubmenuItem {
  key: string;
  label: string;
  to: string;
  isHome?: boolean;
  uniqid?: string;
  children?: SubmenuChild[];
  matchPaths?: string[];
}

export interface AudienceTab {
  key: AudienceKey;
  label: string;
  icon: React.FC<{ className?: string }>;
}

export const AUDIENCE_TABS: AudienceTab[] = [
  { key: 'self', label: __('Pre seba'), icon: ProfileIcon },
  { key: 'schools', label: __('Pre školské zariadenia'), icon: TeacherIcon },
  {
    key: 'organizations',
    label: __('Pre firmy a organizácie'),
    icon: PresentationChartIcon,
  },
];

export function findByUniqid(
  tree: MenuNode[] | undefined | null,
  uniqid: string,
): MenuNode | undefined {
  if (!tree || !tree.length) {
    return undefined;
  }
  for (const node of tree) {
    if (node.uniqid === uniqid) {
      return node;
    }
    const found = findByUniqid(node.children, uniqid);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function findByUrlFragment(
  tree: MenuNode[] | undefined | null,
  fragment: string,
): MenuNode | undefined {
  if (!tree || !tree.length) {
    return undefined;
  }
  for (const node of tree) {
    if (
      (node.url && node.url.indexOf(fragment) !== -1) ||
      (node.link && node.link.indexOf(fragment) !== -1)
    ) {
      return node;
    }
    const found = findByUrlFragment(node.children, fragment);
    if (found) {
      return found;
    }
  }
  return undefined;
}

export function nodeTo(node: MenuNode | undefined, fallback: string): string {
  if (node && (node.link || node.url)) {
    return getUrl(node.link ? node.link : (node.url as string));
  }
  return fallback;
}

function visibleChildren(node: MenuNode | undefined): MenuNode[] {
  if (!node || !node.children) {
    return [];
  }
  return node.children.filter((c) => c.webshow !== 0);
}

export const AUDIENCE_URLS: Record<AudienceKey, string> = {
  self: '/',
  schools: URL_FOR_SCHOOLS,
  organizations: URL_FOR_ORGANIZATIONS,
};

function homeItem(audience: AudienceKey): SubmenuItem {
  return {
    key: 'home',
    label: __('Úvod'),
    to: AUDIENCE_URLS[audience],
    isHome: true,
  };
}

function buildSelfSubmenu(
  tree: MenuNode[],
  skvelkoFirst: boolean = true,
  eshopHomepage: boolean = false,
): SubmenuItem[] {
  const news = findByUniqid(tree, 'SHOP_NETWORK_NEWS');
  const customerCard = findByUniqid(tree, 'CUSTOMER_CARD');
  const giftCard = findByUrlFragment(tree, 'darcekova-karta');
  //const benefits = findByUrlFragment(tree, 'nase-vyhody');
  const supportEducation = findByUniqid(tree, 'SUPPORT_EDUCATION');

  const skvelkoItem: SubmenuItem = {
    key: 'skvelko',
    label: __('Zoznamy pomôcok Skvelko'),
    to: URL_SETS,
    matchPaths: [URL_SET],
  };
  const eshopItem: SubmenuItem = {
    key: 'eshop',
    label: __('E-shop'),
    to: eshopEntryUrl(eshopHomepage),
  };

  return [
    ...(eshopHomepage ? [] : [homeItem('self')]),
    ...(skvelkoFirst ? [skvelkoItem, eshopItem] : [eshopItem, skvelkoItem]),
    {
      key: 'news',
      label: __('Aktuality'),
      to: nodeTo(news, '/siet-papiernictiev/aktuality'),
      uniqid: news?.uniqid,
    },
    {
      key: 'customer-card',
      label: __('Zákaznícka karta'),
      to: nodeTo(customerCard, '/karticka'),
      uniqid: customerCard?.uniqid,
    },
    {
      key: 'gift-card',
      label: __('Darčeková karta'),
      to: nodeTo(giftCard, '/siet-papiernictiev/darcekova-karta-daffer'),
      uniqid: giftCard?.uniqid,
    },
    /*
    {
      key: 'benefits',
      label: __('Naše výhody'),
      to: nodeTo(benefits, '/siet-papiernictiev/nase-vyhody'),
      uniqid: benefits?.uniqid,
    },
    */
    {
      key: 'support-education',
      label: supportEducation?.name || __('Podporujeme vzdelávanie'),
      to: nodeTo(supportEducation, '/pre-seba/podporujeme-vzdelavanie'),
      uniqid: supportEducation?.uniqid,
    },
  ];
}

function buildSectionSubmenu(
  node: MenuNode | undefined,
  audience: AudienceKey,
): SubmenuItem[] {
  const items: SubmenuItem[] = [homeItem(audience)];
  visibleChildren(node).forEach((child, i) => {
    const to = nodeTo(child, '/');
    const childItems = visibleChildren(child).map((gc, j) => ({
      key: `gc-${i}-${j}-${nodeTo(gc, '/')}`,
      label: gc.name || '',
      to: nodeTo(gc, '/'),
      uniqid: gc.uniqid,
    }));
    items.push({
      key: `${child.id || i}-${to}`,
      label: child.name || '',
      to,
      uniqid: child.uniqid,
      children: childItems.length ? childItems : undefined,
    });
  });
  return items;
}

function withSwappedChildren(
  node: MenuNode | undefined,
  uidA: string,
  uidB: string,
): MenuNode | undefined {
  if (!node || !node.children) {
    return node;
  }
  const children = [...node.children];
  const a = children.findIndex((c) => c.uniqid === uidA);
  const b = children.findIndex((c) => c.uniqid === uidB);
  if (a !== -1 && b !== -1) {
    [children[a], children[b]] = [children[b], children[a]];
  }
  return { ...node, children };
}

function eshopEntryUrl(eshopHomepage: boolean): string {
  return eshopHomepage ? '/' : URL_ESHOP_ENTRY;
}

export function buildSubmenu(
  tree: MenuNode[] | undefined | null,
  audience: AudienceKey,
  skvelkoFirst: boolean = true,
  eshopHomepage: boolean = false,
): SubmenuItem[] {
  const safeTree = tree || [];
  switch (audience) {
    case 'schools': {
      const schoolsItems = buildSectionSubmenu(
        withSwappedChildren(
          findByUniqid(safeTree, 'SCHOOLS'),
          'SCHOOL_ARTICLES',
          'SCHOOL_PROPERTY_AND_',
        ),
        'schools',
      );
      schoolsItems[1] = {
        key: 'furniture',
        label: __('Nábytok a vybavenie škôl'),
        to: URL_FURNITURE_SCHOOLS,
      };
      // Insert "E-shop" between "Nábytok a vybavenie škôl" and "Aktuality pre školy".
      schoolsItems.splice(2, 0, {
        key: 'eshop',
        label: __('E-shop'),
        to: eshopEntryUrl(eshopHomepage),
      });
      return schoolsItems;
    }
    case 'organizations': {
      const organizationsItems = buildSectionSubmenu(
        findByUniqid(safeTree, 'ORGANIZATIONS'),
        'organizations',
      );
      organizationsItems.splice(1, 0, {
        key: 'eshop',
        label: __('E-shop'),
        to: eshopEntryUrl(eshopHomepage),
      });
      return organizationsItems;
    }
    case 'self':
    default:
      return buildSelfSubmenu(safeTree, skvelkoFirst, eshopHomepage);
  }
}

export interface UtilityLink {
  key: string;
  label: string;
  to: string;
  icon: React.FC<{ className?: string }>;
  children?: SubmenuChild[];
}

export function buildUtilityLinks(
  tree: MenuNode[] | undefined | null,
): UtilityLink[] {
  const safeTree = tree || [];
  const helpNode = findByUniqid(safeTree, 'HELP_AND_SUPPORT');
  const contactNode = findByUniqid(safeTree, 'CONTACT');
  const subLinks = (
    node: MenuNode | undefined,
    prefix: string,
  ): SubmenuChild[] =>
    visibleChildren(node).map((c, i) => ({
      key: `${prefix}-${i}`,
      label: c.name || '',
      to: nodeTo(c, '/'),
      uniqid: c.uniqid,
    }));
  return [
    {
      key: 'shops',
      label: __('Predajne'),
      to: nodeTo(findByUniqid(safeTree, 'SHOP_NETWORK'), '/predajne'),
      icon: ShopIcon,
    },
    {
      key: 'help',
      label: __('Pomoc'),
      to: nodeTo(helpNode, '/pomoc-a-podpora'),
      icon: MessagesIcon,
      children: subLinks(helpNode, 'help'),
    },
    {
      key: 'contact',
      label: __('Kontakt'),
      to: nodeTo(contactNode, '/kontakt'),
      icon: CallIcon,
      children: subLinks(contactNode, 'contact'),
    },
  ];
}

const UTILITY_SECTIONS: { key: string; uniqid: string }[] = [
  { key: 'shops', uniqid: 'SHOP_NETWORK' },
  { key: 'help', uniqid: 'HELP_AND_SUPPORT' },
  { key: 'contact', uniqid: 'CONTACT' },
];

function collectNodePaths(node: MenuNode | undefined): string[] {
  if (!node) {
    return [];
  }
  const paths: string[] = [];
  if (node.link || node.url) {
    paths.push(nodeTo(node, ''));
  }
  (node.children || []).forEach((child) => {
    paths.push(...collectNodePaths(child));
  });
  return paths.filter(Boolean);
}

export function detectActiveUtilityKey(
  tree: MenuNode[] | undefined | null,
  currentPath: string,
  currentUniqs: string[] = [],
): string | null {
  if (!currentPath || currentPath === '/') {
    return null;
  }
  if (
    buildSelfSubmenu(tree || []).some((it) =>
      isSubmenuItemActive(it, currentPath, currentUniqs),
    )
  ) {
    return null;
  }
  for (const section of UTILITY_SECTIONS) {
    const node = findByUniqid(tree, section.uniqid);
    if (collectNodePaths(node).some((to) => pathMatches(currentPath, to))) {
      return section.key;
    }
    if ((currentUniqs || []).includes(section.uniqid)) {
      return section.key;
    }
  }
  return null;
}

function pathMatches(currentPath: string, to: string): boolean {
  if (!to || to === '/') {
    return false;
  }
  const base = getBaseUrl(to);
  return currentPath === base || currentPath.startsWith(`${base}/`);
}

function itemOrChildMatches(item: SubmenuItem, currentPath: string): boolean {
  return (
    pathMatches(currentPath, item.to) ||
    (item.matchPaths || []).some((p) => pathMatches(currentPath, p)) ||
    (item.children || []).some((c) => pathMatches(currentPath, c.to))
  );
}

export function detectAudienceStrict(
  tree: MenuNode[] | undefined | null,
  currentPath: string,
  currentUniqs: string[] = [],
): AudienceKey | null {
  if (!currentPath || currentPath === '/') {
    return 'self';
  }
  const sectionMatches = (audience: AudienceKey) =>
    buildSubmenu(tree, audience)
      .filter((it) => it.key !== 'eshop' && it.key !== 'furniture')
      .some((it) => isSubmenuItemActive(it, currentPath, currentUniqs));
  if (sectionMatches('schools')) {
    return 'schools';
  }
  if (sectionMatches('organizations')) {
    return 'organizations';
  }
  if (sectionMatches('self')) {
    return 'self';
  }
  return null;
}

export function detectAudience(
  tree: MenuNode[] | undefined | null,
  currentPath: string,
  currentUniqs: string[] = [],
): AudienceKey {
  return detectAudienceStrict(tree, currentPath, currentUniqs) ?? 'self';
}

export function audienceForUser(user: any): AudienceKey | null {
  if (isSchoolOrTeacher(user)) {
    return 'schools';
  }
  if (isCompanyOrChildCompany(user)) {
    return 'organizations';
  }
  return null;
}

export function homeUrlForCurrentTab(
  tree?: MenuNode[] | null,
  currentPath: string = '',
  currentUniqs: string[] = [],
  allowCookie: boolean = true,
): string {
  const audience =
    detectAudienceStrict(tree, currentPath, currentUniqs) ??
    (allowCookie ? loadAudienceCookie() : null) ??
    'self';
  return AUDIENCE_URLS[audience];
}

export function isSubmenuItemActive(
  item: SubmenuItem,
  currentPath: string,
  currentUniqs: string[] = [],
): boolean {
  if (item.isHome) {
    if (item.to === '/') {
      return currentPath === '/' || currentPath === '';
    }
    return currentPath === getBaseUrl(item.to);
  }
  const uniqs = currentUniqs || [];
  if (item.uniqid && uniqs.includes(item.uniqid)) {
    return true;
  }
  if ((item.children || []).some((c) => c.uniqid && uniqs.includes(c.uniqid))) {
    return true;
  }
  return itemOrChildMatches(item, currentPath);
}

function categoryInGroup(group: any, categoryId?: number): boolean {
  if (!group || categoryId == null) {
    return false;
  }
  const inList = (cats: any[]): boolean =>
    (cats || []).some(
      (c) =>
        c &&
        (c.category_id === categoryId || (c.children && inList(c.children))),
    );
  return (
    inList(group.categories || []) ||
    (group.subgroups || []).some((sg: any) => inList(sg.categories || []))
  );
}

export function isFurnitureSection(
  categoriesGroup: any[] | undefined | null,
  currentCategoryGroupId?: number,
  currentCategoryId?: number,
): boolean {
  const group = (categoriesGroup || []).find(
    (g) => g && g.uniqid === FURNITURE_CATEGORY_GROUP_UNIQID,
  );
  if (!group) {
    return false;
  }
  if (currentCategoryGroupId != null && currentCategoryGroupId === group.id) {
    return true;
  }
  return categoryInGroup(group, currentCategoryId);
}

export const AUDIENCE_COOKIE = 'audienceTab';

export function loadAudienceCookie(): AudienceKey | null {
  const value = cookie.load(AUDIENCE_COOKIE);
  return AUDIENCE_TABS.some((t) => t.key === value)
    ? (value as AudienceKey)
    : null;
}

export function saveAudienceCookie(value: AudienceKey): void {
  cookie.save(AUDIENCE_COOKIE, value, { path: '/' });
}

export const AUDIENCE_QUERY_PARAM = 'tab';

/** The values campaigns use. Add a line here when a tab is added. */
const AUDIENCE_QUERY_VALUES: Record<string, AudienceKey> = {
  'pre-seba': 'self',
  'pre-skolske-zariadenia': 'schools',
  'pre-firmy-a-organizacie': 'organizations',
};

const MAILCHIMP_SCHOOL_PARAM = 'mailchimp';

function queryValue(raw: any): string {
  return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

export function audienceFromQuery(query: any): AudienceKey | null {
  if (!query) {
    return null;
  }
  const tab = AUDIENCE_QUERY_VALUES[queryValue(query[AUDIENCE_QUERY_PARAM])];
  if (tab) {
    return tab;
  }
  if (queryValue(query[MAILCHIMP_SCHOOL_PARAM]) === 'school') {
    return 'schools';
  }
  return null;
}

export interface Crumb {
  name?: string;
  url?: string;
  link?: string;
  uniqid?: string;
}

export interface BreadcrumbContext {
  mainMenu?: MenuNode[] | null;
  currentPath?: string;
  currentUniqs?: string[] | null;
  categoriesGroup?: any[] | null;
  currentCategoryGroupId?: number;
  currentCategoryId?: number;
  eshopHomepage?: boolean;
}

function withEshopHomepageUrl(crumb: Crumb): Crumb {
  const url = crumb.link || crumb.url;
  if (!url || getBaseUrl(url) !== URL_ESHOP_ENTRY) {
    return crumb;
  }
  return { ...crumb, url: '/', link: undefined };
}

export function buildBreadcrumbs(
  items: Crumb[] | undefined | null,
  ctx: BreadcrumbContext,
): Crumb[] {
  const body: Crumb[] = (items || []).map((c) =>
    ctx.eshopHomepage ? withEshopHomepageUrl(c) : c,
  );
  const currentPath = ctx.currentPath || '';
  const currentUniqs = ctx.currentUniqs || [];

  const furniture = isFurnitureSection(
    ctx.categoriesGroup,
    ctx.currentCategoryGroupId,
    ctx.currentCategoryId,
  );
  const eshop = !furniture && (ctx.currentCategoryGroupId ?? -1) > 0;

  const onUtilitySection = !!detectActiveUtilityKey(
    ctx.mainMenu,
    currentPath,
    currentUniqs,
  );

  const audience = onUtilitySection
    ? null
    : detectAudienceStrict(ctx.mainMenu, currentPath, currentUniqs) ||
      loadAudienceCookie() ||
      'self';
  const audienceUrl = audience ? AUDIENCE_URLS[audience] : '';
  const audienceTab = audience
    ? AUDIENCE_TABS.find((t) => t.key === audience)
    : undefined;
  const audienceLabel = audienceTab ? audienceTab.label : '';

  const prefix: Crumb[] = [];
  if (audience) {
    prefix.push({ name: audienceLabel, url: audienceUrl });
    if (eshop) {
      prefix.push({
        name: __('E-shop'),
        url: eshopEntryUrl(!!ctx.eshopHomepage),
      });
    }
  }

  const SELF_SHOP_KEYS = ['news', 'customer-card', 'gift-card', 'benefits'];
  const onSelfShopPage = buildSelfSubmenu(ctx.mainMenu || [])
    .filter((it) => SELF_SHOP_KEYS.indexOf(it.key) !== -1)
    .some((it) => isSubmenuItemActive(it, currentPath, currentUniqs));

  const isGenericRoot = (c: Crumb): boolean => {
    const name = (c.name || '').trim();
    if (name === 'Úvod' || name === 'Internetový obchod') {
      return true;
    }
    if (c.uniqid === 'SCHOOLS' || c.uniqid === 'ORGANIZATIONS') {
      return true;
    }
    if (onSelfShopPage && c.uniqid === 'SHOP_NETWORK') {
      return true;
    }
    if (audienceLabel && name === audienceLabel) {
      return true;
    }
    const url = c.link || c.url;
    if (url && audienceUrl && getBaseUrl(url) === getBaseUrl(audienceUrl)) {
      return true;
    }
    return false;
  };
  while (body.length && isGenericRoot(body[0])) {
    body.shift();
  }

  const prefixLast = prefix[prefix.length - 1];
  const prefixLastUrl = prefixLast
    ? prefixLast.link || prefixLast.url
    : undefined;
  const bodyFirstUrl = body.length ? body[0].link || body[0].url : undefined;
  if (
    prefixLastUrl &&
    bodyFirstUrl &&
    getBaseUrl(bodyFirstUrl) === getBaseUrl(prefixLastUrl)
  ) {
    body.shift();
  }

  return [...prefix, ...body];
}
